import { LogEntry, LogTransport } from '../types';

interface StoredLogEntry extends LogEntry {
  id: string;
}

export class DevFileTransport implements LogTransport {
  private dbName = 'spa-ui-logs';
  private storeName = 'logs';
  private maxEntries = 5000;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;
        const objectStore = db.createObjectStore(this.storeName, {
          keyPath: 'id',
        });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      };
    });
  }

  async log(entry: LogEntry): Promise<void> {
    try {
      // Store in IndexedDB
      await this.storeInDB(entry);
    } catch (error) {
      console.error('DevFileTransport failed:', error);
    }
  }

  private async storeInDB(entry: LogEntry): Promise<void> {
    if (!this.initPromise) return;
    await this.initPromise;

    if (!this.db) return;

    const storedEntry: StoredLogEntry = {
      ...entry,
      id: `${entry.timestamp}-${Math.random().toString(36).substr(2, 9)}`,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const request = store.add(storedEntry);

      request.onsuccess = () => {
        // Clean up old entries if we exceed max
        this.cleanupOldEntries();
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  private async cleanupOldEntries(): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const countRequest = store.count();
    countRequest.onsuccess = () => {
      const count = countRequest.result;
      if (count > this.maxEntries) {
        // Remove oldest entries
        const entriesToRemove = count - this.maxEntries;
        const index = store.index('timestamp');
        const cursorRequest = index.openCursor();

        let removed = 0;
        cursorRequest.onsuccess = event => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor && removed < entriesToRemove) {
            cursor.delete();
            removed++;
            cursor.continue();
          }
        };
      }
    };
  }

  async getAllLogs(): Promise<LogEntry[]> {
    if (!this.initPromise) return [];
    await this.initPromise;

    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const request = index.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async downloadLogs(): Promise<void> {
    const logs = await this.getAllLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `spa-ui-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  async clearLogs(): Promise<void> {
    if (!this.initPromise) return;
    await this.initPromise;

    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
